// ADS-B traffic layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 round 2 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { ensureTrafficIcons } from '../trafficIcons';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the traffic source + its layers. Idempotent on the source.
 */
export function addTrafficLayers(map: MlMap): void {
    if (!map.getSource('traffic')) {
      map.addSource('traffic', { type: 'geojson', data: EMPTY_FC });
      void ensureTrafficIcons(map);
      map.addLayer({
        id: 'traffic-icon',
        type: 'symbol',
        source: 'traffic',
        minzoom: 3,
        layout: {
          'icon-image': [
            'match', ['get', 'colorClass'],
            'above',      'traffic-above',
            'near-above', 'traffic-near-above',
            'coalt',      'traffic-coalt',
            'near-below', 'traffic-near-below',
            'below',      'traffic-below',
            'emergency',  'traffic-emergency',
            'traffic-coalt',
          ],
          'icon-rotate': ['get', 'trackDeg'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            3,  0.35,
            6,  0.55,
            9,  0.80,
            12, 1.00,
            14, 1.15,
          ],
        },
        filter: ['==', ['geometry-type'], 'Point'],
      });
      map.addLayer({
        id: 'traffic-label',
        type: 'symbol',
        source: 'traffic',
        minzoom: 9,
        layout: {
          'text-field': [
            'concat',
            ['coalesce', ['get', 'callsign'], ['get', 'registration'], ''],
            '\n',
            ['case',
              ['==', ['get', 'source'], 'internet'], 'INET ',
              ['==', ['get', 'source'], 'gdl90'], 'GDL90 ',
              '',
            ],
            ['case',
              ['has', 'altText'], ['get', 'altText'],
              '',
            ],
          ],
          'text-size': 10,
          'text-offset': [0, 1.6],
          'text-anchor': 'top',
          'text-font': ['Noto Sans Regular'],
          'text-allow-overlap': false,
          'text-optional': true,
        },
        paint: {
          'text-color': '#e8edf3',
          'text-halo-color': '#0b0d0f',
          'text-halo-width': 1.4,
        },
        filter: ['==', ['geometry-type'], 'Point'],
      });
    }
}

registerLayerFamily({
  id: 'traffic',
  label: 'ADS-B traffic',
  sourceId: 'traffic',
  layerIds: ['traffic-icon', 'traffic-label'],
  mount: (map) => addTrafficLayers(map),
  // NOTE: TrafficController owns visibility via useLayerVisibility().
  // We add applyState here ONLY for basemap-rebuild restoration.
  applyState: (map) => {
    if (!map.getLayer('traffic-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.traffic;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('traffic-icon', 'visibility', v);
    map.setLayoutProperty('traffic-label', 'visibility', v);
  },
  // No visibilitySelector: TrafficController manages visibility reactively.
});
